const END_OF_FIELD_VALUE = "metadata_tag.end_of_field_value";
const OPEN_PARENTHESES = "metadata_tag.open_parentheses";
const CLOSE_PARENTHESES = "metadata_tag.close_parentheses";
const DATE_SEPARATOR = "metadata_tag.date_separator";
const FIELD_VALUE_SEPARATOR = "metadata_tag.field_value_separator";
const END_OF_TITLE_VALUE = "metadata_tag.end_of_title";
const SITE_NAME = "metadata_tag.site_name";
const KEYS = "metadata_tag.keys";
const YES = "metadata_tag.yes";
const NO = "metadata_tag.no";
import { convertToTitleCase } from "@thegetty-private/linked-data.js";

/**
 * Gets an elasticsearch object and returns metadata information extracted from it
 *
 * @param {object} elasticsearchManMadeObject - Object with metadata information
 * @return {object} Map of objects used to create manMadeObject metadata tags [{fieldName, value}]
 */
export function getHumanMadeObjectMetadata(
  elasticsearchManMadeObject,
  translate
) {
  let objectMetadata = new Object();
  //add static string values first
  objectMetadata["keywords"] = translate(KEYS);
  objectMetadata["og:site_name"] = translate(SITE_NAME, { title: "" });
  //check if ES object is empty
  if (
    !elasticsearchManMadeObject ||
    Object.keys(elasticsearchManMadeObject).length == 0
  ) {
    return objectMetadata;
  }
  let manMadeObjectTitle = elasticsearchManMadeObject.primary_name;
  if (manMadeObjectTitle) {
    objectMetadata["title"] = manMadeObjectTitle;
    objectMetadata["name"] = manMadeObjectTitle;
    objectMetadata["og:title"] = translate(END_OF_TITLE_VALUE, {
      title: manMadeObjectTitle
    });
    objectMetadata["twitter:title"] = translate(END_OF_TITLE_VALUE, {
      title: manMadeObjectTitle
    });
  }

  let metaDescription = elasticsearchManMadeObject.meta_description;
  if (metaDescription) {
    let combinedDescription = `${manMadeObjectTitle}; ${metaDescription}`;
    objectMetadata["og:description"] = combinedDescription;
    objectMetadata["twitter:description"] = combinedDescription;
  }

  let manMadeObjectMedium = elasticsearchManMadeObject.materials;
  if (manMadeObjectMedium) {
    objectMetadata["medium"] = manMadeObjectMedium;
  }
  let manMadeObjectCreated = elasticsearchManMadeObject.date_created;
  if (manMadeObjectCreated) {
    //date of human made object
    objectMetadata["date"] = manMadeObjectCreated;
  }
  let manMadeObjectDimensions = elasticsearchManMadeObject.dimensions;
  if (manMadeObjectDimensions) {
    objectMetadata["dimensions"] = manMadeObjectDimensions;
  }
  let manMadeObjectNumber = elasticsearchManMadeObject.object_number;
  if (manMadeObjectNumber) {
    objectMetadata["object_number"] = manMadeObjectNumber;
  }
  let manMadeObjectCulture = getMetaArrayValue(
    elasticsearchManMadeObject,
    "culture"
  );
  if (manMadeObjectCulture) {
    objectMetadata["culture"] = manMadeObjectCulture;
  }

  let manMadeObjectThumbnail = elasticsearchManMadeObject.thumbnail;
  let metaTagThumbnail;
  if (
    elasticsearchManMadeObject.manifest &&
    elasticsearchManMadeObject.manifest.thumb
  ) {
    metaTagThumbnail = elasticsearchManMadeObject.manifest.thumb;
  } else if (manMadeObjectThumbnail) {
    metaTagThumbnail = manMadeObjectThumbnail;
  }
  setHumanMadeObjectThumbnail(objectMetadata, metaTagThumbnail);

  let manMadeObjectPlaceCreated = getMetaArrayValue(
    elasticsearchManMadeObject,
    "place_created"
  );
  if (manMadeObjectPlaceCreated) {
    objectMetadata["place_created"] = manMadeObjectPlaceCreated;
  }
  let manMadeObjectType = elasticsearchManMadeObject.object_type;
  if (manMadeObjectType) {
    objectMetadata["object_type"] = manMadeObjectType;
  }
  if (elasticsearchManMadeObject.department) {
    objectMetadata["department"] = translate("metaTagHelpers.department", {
      department: convertToTitleCase(elasticsearchManMadeObject.department)
    });
  }

  if (elasticsearchManMadeObject.classification) {
    //the value of classification is the first word of department
    objectMetadata["classification"] = convertToTitleCase(
      elasticsearchManMadeObject.classification
    );
  }

  if (elasticsearchManMadeObject.open_content == true) {
    objectMetadata["open_content"] = translate(YES);
  }
  if (elasticsearchManMadeObject.center == true) {
    objectMetadata["on_view"] = translate(YES);
    objectMetadata["location"] = "gc";
  }
  if (elasticsearchManMadeObject.villa == true) {
    objectMetadata["on_view"] = translate(YES);
    objectMetadata["location"] = "villa";
  }

  // add maker (producer) info
  const manMadeObjectProducers = elasticsearchManMadeObject.producers;
  let producerDescriptionsString = undefined;

  if (manMadeObjectProducers) {
    let producerDescriptions = [];

    manMadeObjectProducers.forEach((producer) => {
      producerDescriptions.push(producer.description);
    });

    producerDescriptionsString = producerDescriptions.join(", ");

    objectMetadata["maker"] = producerDescriptionsString;
  }

  objectMetadata["description"] = translate(SITE_NAME, {
    title: getManMadeObjectArtistDescription({
      title: manMadeObjectTitle,
      date: manMadeObjectCreated,
      medium: manMadeObjectMedium,
      dimensions: manMadeObjectDimensions,
      objectNumber: manMadeObjectNumber,
      producer: producerDescriptionsString,
      delimiter: translate(END_OF_FIELD_VALUE)
    })
  });

  //TODO: image link is missing
  return objectMetadata;
}
/**
 * Gets an elasticsearch object and returns metadata information extracted from it
 *
 * @param {object} elasticsearchArtist - Object with metadata information
 * @return {object} Map of objects used to create artist metadata tags [{fieldName, value}]
 */
export function getArtistMetadata(elasticsearchArtist, translate) {
  let objectMetadata = new Object();
  //add static string values first
  objectMetadata["keywords"] = translate(KEYS);
  objectMetadata["og:site_name"] = translate(SITE_NAME, { title: "" });
  //check if ES object is empty
  if (!elasticsearchArtist || Object.keys(elasticsearchArtist).length == 0) {
    return objectMetadata;
  }
  objectMetadata["open_content"] = translate(NO);
  let artistName = elasticsearchArtist.primary_name;
  if (artistName) {
    objectMetadata["name"] = artistName;
  }
  let artistNationality = getMetaArrayValue(elasticsearchArtist, "nationality");
  if (artistNationality) {
    objectMetadata["culture"] = artistNationality;
  }
  objectMetadata["date"] = getDateValue(
    elasticsearchArtist,
    translate(DATE_SEPARATOR)
  );
  let artistTitle = getArtistTitle(
    elasticsearchArtist,
    objectMetadata["date"],
    translate(OPEN_PARENTHESES),
    translate(CLOSE_PARENTHESES),
    translate(FIELD_VALUE_SEPARATOR)
  );
  if (artistTitle) {
    objectMetadata["maker"] = artistTitle;
    objectMetadata["title"] = translate(END_OF_TITLE_VALUE, {
      title: artistTitle
    });
    objectMetadata["og:title"] = objectMetadata["title"];
    objectMetadata["og:description"] = translate(SITE_NAME, {
      title: artistTitle
    });
    objectMetadata["twitter:title"] = objectMetadata["title"];
    objectMetadata["twitter:description"] = translate(SITE_NAME, {
      title: artistTitle
    });
    objectMetadata["description"] = translate(SITE_NAME, {
      title: artistTitle
    });
  }
  //TODO: image link is missing
  return objectMetadata;
}
/**
 * @param {object} artistInfo - An indexed Elasticsearch person/artist object
 * Only being used for elasticsearch Artist objects (despite manMadeObject var names)
 *
 * @return {string} An object's maker information to be used in the <head> metadata
 *
 * note: replicating artist's information on current Collection Online
 */
export function getArtistTitle(
  artist,
  date,
  open_paren,
  close_paren,
  field_value_separator
) {
  if (!artist) {
    return;
  }
  let manMadeObjectArtistNationality = getMetaArrayValue(artist, "nationality");
  let manMadeObjectArtistDate = date;
  let artistInfo = "";
  if (artist["primary_name"]) {
    artistInfo = artist["primary_name"];
  }
  if (manMadeObjectArtistNationality) {
    artistInfo += open_paren + manMadeObjectArtistNationality;
  }
  if (manMadeObjectArtistDate) {
    artistInfo += field_value_separator + manMadeObjectArtistDate;
  }
  if (artistInfo.includes(open_paren)) {
    artistInfo += close_paren;
  }
  return artistInfo;
}

/**
 * @param {object} object - An indexed Elasticsearch manMadeObject
 *
 * @return {string} A description to be used in the <head> metadata
 *
 * todo: To match current site's manMadeObject metadata more closely, complete DEV-2773
 */
export function getManMadeObjectArtistDescription({
  title,
  date,
  medium,
  dimensions,
  objectNumber,
  producer,
  delimiter
}) {
  let description = [];
  if (title) {
    description.push(title);
  }
  if (date) {
    description.push(date);
  }
  if (producer) {
    description.push(producer);
  }
  if (medium) {
    description.push(medium);
  }
  if (dimensions) {
    if (Array.isArray(dimensions)) {
      description.push(dimensions[0]);
    } else {
      description.push(dimensions);
    }
  }
  if (objectNumber) {
    description.push(objectNumber);
  }
  return description.join(delimiter);
}
/**
 * @param {object} object - An indexed Elasticsearch person/artist object and fieldName
 *
 * @return {string} The value of the fields value extracted from object to be used in the <head> metadata
 *
 */

export function getMetaArrayValue(object, fieldName) {
  if (object[fieldName] == undefined) {
    return;
  }
  if (object[fieldName].length == 0) {
    return;
  }
  return object[fieldName][0];
}
/**
 * @param {object} object - An indexed Elasticsearch object
 *
 * @return {string} The value of the artist date extracted from the ES object to be used in the <head> metadata
 *
 */

export function getDateValue(object, date_separator) {
  let artistDate = "";
  let artistBirth = object.born;
  if (artistBirth) {
    artistDate = artistBirth;
  }
  let artistDeath = object.died;
  if (artistDeath) {
    artistDate += date_separator + artistDeath;
  }
  return artistDate;
}

/**
 * @param {object} object - An indexed Elasticsearch object
 *
 * @return {Array} An array with metadata tag information to display in html
 *
 */
export function setMetaValues(metadata) {
  let metaTagInformation = []; //add metadata object field and field values to an array to display in metadata tag html
  for (let field in metadata) {
    if (metadata[field] == undefined || metadata[field].length == 0) {
      continue;
    }
    metaTagInformation.push({
      name: field,
      content: metadata[field]
    });
  }
  return metaTagInformation;
}

/**
 * If there is an image thumbnail available, sets all relevant meta tags with that value
 *
 * @param {object} objectMetadata - map of HMO metadata fields
 * @param {string} thumbnail - the image thumbnail url to use
 */
export function setHumanMadeObjectThumbnail(objectMetadata, thumbnail) {
  let thumbnailFields = [
    "thumbnail",
    "og:image",
    "og:image:secure_url",
    "twitter:image",
    "twitter:image:secure_url"
  ];

  thumbnailFields.forEach((field) => (objectMetadata[field] = thumbnail));
}
